/* Postura scroll-360: scrubs a frame sequence as the page scrolls, then hands off to the buy panel. */
(function () {
  'use strict';

  class Scroll360 extends HTMLElement {
    connectedCallback() {
      this.canvas = this.querySelector('canvas');
      this.stage = this.querySelector('[data-stage]');
      this.beats = Array.from(this.querySelectorAll('[data-beat]'));
      this.progressBar = this.querySelector('[data-progress]');
      this.hint = this.querySelector('[data-hint]');
      this.ctx = this.canvas.getContext('2d');
      const large = JSON.parse(this.querySelector('[data-frames]').textContent);
      const smallEl = this.querySelector('[data-frames-small]');
      const small = smallEl ? JSON.parse(smallEl.textContent) : large;
      // Phones get the lighter 1200px set and a lower canvas density; decoding 1440px frames on every scroll tick is what makes phones stutter.
      this.isSmall = window.innerWidth < 768;
      this.frameUrls = this.isSmall ? small : large;
      this.frames = new Array(this.frameUrls.length);
      this.loaded = 0;
      this.current = -1;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.tick = this.tick.bind(this);

      this.onResize();
      window.addEventListener('resize', this.onResize, { passive: true });
      // Capture phase so scrolling inside any container (not just the window) drives the sequence.
      document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });

      this.loadFrame(0, () => {
        this.draw(0);
        this.preloadRest();
      });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('scroll', this.onScroll, { capture: true });
    }

    loadFrame(i, cb) {
      if (this.frames[i]) { cb && cb(); return; }
      const img = new Image();
      img.decoding = 'async';
      const done = () => {
        this.frames[i] = img;
        this.loaded++;
        if (cb) cb();
        if (this.pending === i) { this.pending = null; this.draw(i); }
      };
      img.onload = () => {
        // Decode off the main thread where supported so drawImage never stalls the scroll.
        if (img.decode) { img.decode().then(done, done); } else { done(); }
      };
      img.onerror = () => { if (cb) cb(); };
      img.src = this.frameUrls[i];
    }

    preloadRest() {
      // Load in a spread order so scrubbing feels responsive before everything is in.
      const n = this.frameUrls.length;
      const order = [];
      for (let step = Math.ceil(n / 2); step >= 1; step = Math.floor(step / 2)) {
        for (let i = 0; i < n; i += step) if (!order.includes(i)) order.push(i);
        if (step === 1) break;
      }
      let idx = 0;
      const next = () => {
        if (idx >= order.length) return;
        const i = order[idx++];
        if (this.frames[i]) return next();
        this.loadFrame(i, next);
      };
      // A few parallel lanes.
      next(); next(); next(); next();
    }

    onResize() {
      const dpr = Math.min(window.devicePixelRatio || 1, this.isSmall ? 1.5 : 2);
      const rect = this.stage.getBoundingClientRect();
      const w = Math.round(rect.width * dpr);
      const h = Math.round(rect.height * dpr);
      // Phones fire resize while the address bar collapses. If the stage's pixel size has not changed, do nothing:
      // resizing the canvas clears it and causes a white flash mid-scroll.
      if (w === this.canvas.width && h === this.canvas.height) return;
      this.canvas.width = w;
      this.canvas.height = h;
      this.canvas.style.width = rect.width + 'px';
      this.canvas.style.height = rect.height + 'px';
      const i = this.current;
      this.current = -1;
      if (i >= 0 && this.frames[i]) {
        this.draw(i); // redraw synchronously so the canvas never shows empty
      } else {
        this.onScroll();
      }
    }

    progress() {
      const rect = this.getBoundingClientRect();
      const total = this.offsetHeight - this.stage.offsetHeight;
      if (total <= 0) return 0;
      return Math.min(1, Math.max(0, -rect.top / total));
    }

    onScroll() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(this.tick);
    }

    tick() {
      this.raf = null;
      const p = this.progress();
      const n = this.frameUrls.length;
      // Reserve the last 12% of the scroll for the hand-off to the buy panel.
      const turn = Math.min(1, p / 0.88);
      const i = this.reduced ? 0 : Math.min(n - 1, Math.round(turn * (n - 1)));
      if (i !== this.current) this.draw(i);

      if (this.progressBar) this.progressBar.style.transform = 'scaleX(' + turn + ')';
      if (this.hint) this.hint.classList.toggle('is-hidden', p > 0.04);

      for (const beat of this.beats) {
        const start = parseFloat(beat.dataset.start);
        const end = parseFloat(beat.dataset.end);
        const on = p >= start && p <= end;
        if (beat.classList.contains('is-active') !== on) beat.classList.toggle('is-active', on);
      }
    }

    draw(i) {
      const img = this.frames[i];
      if (!img) { this.pending = i; return; }
      this.current = i;
      const c = this.canvas, ctx = this.ctx;
      const cw = c.width, ch = c.height;
      const iw = img.naturalWidth, ih = img.naturalHeight;
      // "cover" on narrow screens, "contain" on wide screens so the figure is never cropped on desktop.
      const wide = cw / ch > iw / ih;
      const mode = this.dataset.fit || 'auto';
      let scale;
      if (mode === 'cover' || (mode === 'auto' && !wide)) {
        scale = Math.max(cw / iw, ch / ih);
      } else {
        scale = Math.min(cw / iw, ch / ih);
      }
      const dw = iw * scale, dh = ih * scale;
      const dx = (cw - dw) / 2, dy = (ch - dh) / 2;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.fillStyle = this.dataset.bg || '#ffffff';
      ctx.fillRect(0, 0, cw, ch);
      ctx.drawImage(img, dx, dy, dw, dh);
    }
  }

  if (!customElements.get('scroll-360')) customElements.define('scroll-360', Scroll360);

  /* Buy panel: variant pills, price, optional add-on, buy now -> checkout. */
  class Buy360 extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.pills = Array.from(this.querySelectorAll('[data-variant-id]'));
      this.price = this.querySelector('[data-price]');
      this.compare = this.querySelector('[data-compare]');
      this.idInput = this.querySelector('input[name="id"]');
      this.addon = this.querySelector('[data-addon]');
      this.buyNow = this.querySelector('[data-buy-now]');
      this.addToCart = this.querySelector('[data-add-to-cart]');
      this.error = this.querySelector('[data-error]');
      this.money = this.dataset.moneyFormat || '${{amount}}';

      this.pills.forEach((pill) => pill.addEventListener('click', () => this.select(pill.dataset.variantId)));
      if (this.buyNow) this.buyNow.addEventListener('click', (e) => { e.preventDefault(); this.submit('/checkout'); });
      if (this.addToCart) this.addToCart.addEventListener('click', (e) => { e.preventDefault(); this.submit('/cart'); });

      const first = this.variants.find((v) => v.available) || this.variants[0];
      if (first) this.select(String(first.id));
    }

    format(cents) {
      const amount = (cents / 100).toFixed(2);
      return this.money.replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
    }

    select(id) {
      const v = this.variants.find((x) => String(x.id) === String(id));
      if (!v) return;
      this.selected = v;
      this.idInput.value = v.id;
      this.pills.forEach((p) => {
        const on = p.dataset.variantId === String(v.id);
        p.classList.toggle('is-selected', on);
        p.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.price.textContent = this.format(v.price);
      if (this.compare) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          this.compare.textContent = this.format(v.compare_at_price);
          this.compare.hidden = false;
        } else {
          this.compare.hidden = true;
        }
      }
      const soldOut = !v.available;
      [this.buyNow, this.addToCart].forEach((b) => { if (b) { b.disabled = soldOut; } });
      if (this.buyNow) this.buyNow.textContent = soldOut ? this.buyNow.dataset.soldOut : this.buyNow.dataset.label;
    }

    async submit(destination) {
      if (!this.selected) return;
      const items = [{ id: this.selected.id, quantity: 1 }];
      if (this.addon && this.addon.checked && this.addon.value) {
        items.push({ id: parseInt(this.addon.value, 10), quantity: 1 });
      }
      const buttons = [this.buyNow, this.addToCart].filter(Boolean);
      buttons.forEach((b) => { b.disabled = true; b.classList.add('is-loading'); });
      if (this.error) this.error.hidden = true;
      try {
        const res = await fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Could not add to cart.');
        }
        window.location.href = destination;
      } catch (err) {
        if (this.error) { this.error.textContent = err.message; this.error.hidden = false; }
        buttons.forEach((b) => { b.disabled = false; b.classList.remove('is-loading'); });
      }
    }
  }

  if (!customElements.get('buy-360')) customElements.define('buy-360', Buy360);
})();
